import Pusher, { type ChannelAuthorizationHandler } from 'pusher-js';
import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import { PusherMessage, RoleMessageData } from '@/types/plugins/pusher.types';

export type PusherChannel = ReturnType<Pusher['subscribe']>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PusherEventHandler = (data: any) => void;

export type SubscribeOptions = {
  /** Signs a private channel's subscription; a public channel is never asked. */
  authorize?: ChannelAuthorizationHandler;
};

export type BindEventOptions = {
  /**
   * `false` keeps the payload out of `messages`, which grows for the whole session: for a channel
   * whose payloads carry organisation data, which nothing should hold once it has been applied.
   */
  retainPayload?: boolean;
};

export const usePusherStore = defineStore('pusher', {
  state: () => ({
    pusherInstance: null as Pusher | null,
    connectionStatus: 'disconnected' as 'connecting' | 'connected' | 'disconnected' | 'error',
    messages: [] as PusherMessage[],
    subscribedChannels: {} as Record<string, PusherChannel>,
    showModal: false,
    roleMessage: {} as RoleMessageData,
    // A private channel's signer, by channel name, for the instance's one authorization handler.
    channelAuthorizers: {} as Record<string, ChannelAuthorizationHandler>,
  }),
  actions: {
    /**
     * Initializes the Pusher client. Should be called once at application startup.
     * @param {string} appKey - Your Pusher application key.
     * @param {string} cluster - Your Pusher application cluster (e.g., 'eu', 'us2').
     */
    initializePusher(appKey: string, cluster: string) {
      if (this.pusherInstance) {
        console.warn('Pusher already initialized. Skipping re-initialization.');
        return;
      }

      this.connectionStatus = 'connecting';

      this.pusherInstance = new Pusher(appKey, {
        cluster: cluster,
        // pusher-js takes one handler for every private channel, so it asks the signer the
        // subscriber registered for that channel. Public channels never reach it.
        channelAuthorization: {
          customHandler: (params, callback) => {
            const authorize = this.channelAuthorizers[params.channelName];
            if (authorize) {
              authorize(params, callback);
            } else {
              callback(new Error(`No authorizer for channel '${params.channelName}'.`), null);
            }
          },
        },
      });

      this.pusherInstance.connection.bind(
        'state_change',
        (states: { current: string; previous: string }) => {
          this.connectionStatus = states.current as
            | 'connecting'
            | 'connected'
            | 'disconnected'
            | 'error';
          console.log(`Pusher connection state changed: ${states.previous} -> ${states.current}`);
        },
      );

      this.pusherInstance.connection.bind('connected', () => {
        this.connectionStatus = 'connected';
        console.log('Pusher connected successfully!');
      });

      this.pusherInstance.connection.bind('disconnected', () => {
        this.connectionStatus = 'disconnected';
        console.log('Pusher disconnected.');
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.pusherInstance.connection.bind('error', (err: any) => {
        this.connectionStatus = 'error';
        console.error('Pusher connection error:', err);

        const errorCode = err?.error?.data?.code;
        const errorMessage = err?.error?.data?.message;

        if (errorCode === 4004) {
          console.error('Pusher: App key is invalid or does not exist.');
        } else if (errorCode) {
          console.error(`Pusher error code: ${errorCode}`);
        }

        if (errorMessage) {
          console.error(`Pusher error message: ${errorMessage}`);
        }
      });
    },

    /**
     * Starts the session's client from the app's configuration, unless one is already running:
     * at bootstrap, and again at an in-tab sign-in after a logout has disconnected the last one.
     */
    connect() {
      if (this.pusherInstance) return;

      this.initializePusher(
        import.meta.env.VITE_APP_PUSHER_APP_KEY as string,
        import.meta.env.VITE_APP_PUSHER_APP_CLUSTER as string,
      );
    },

    showRoleEventModal(visible: boolean) {
      this.showModal = visible;
    },

    /**
     * Subscribes to a specified Pusher channel.
     * @param {string} channelName - The name of the channel to subscribe to.
     * @param {SubscribeOptions} [options] - `authorize` signs a private channel's subscription.
     * @returns {PusherChannel | null} The Pusher Channel object, or null if Pusher is not initialized.
     */
    subscribeToChannel(channelName: string, options: SubscribeOptions = {}): PusherChannel | null {
      if (!this.pusherInstance) {
        console.error('Pusher instance not initialized. Call initializePusher first.');
        return null;
      }

      // Registered before subscribing: a connected client asks for the signature at once.
      if (options.authorize) this.channelAuthorizers[channelName] = options.authorize;

      if (this.subscribedChannels[channelName]) {
        console.warn(
          `Already subscribed to channel: ${channelName}. Returning existing channel instance.`,
        );
        return this.subscribedChannels[channelName];
      }

      const channel = this.pusherInstance.subscribe(channelName);
      this.subscribedChannels[channelName] = channel;
      console.log(`Subscribed to channel: ${channelName}`);
      return channel;
    },

    /**
     * Binds a callback function to a specific event on a channel.
     * Adds received messages to the store's `messages` array unless told not to.
     * @param {string} channelName - The name of the channel.
     * @param {string} eventName - The name of the event to bind to.
     * @param {function} [callback] - An optional callback function to execute when the event is received.
     * @param {BindEventOptions} [options] - `retainPayload: false` keeps the payload out of `messages`.
     * @returns {PusherEventHandler | null} The handler bound, for `unbindEvent`, or null if the channel is not subscribed.
     */
    bindEvent(
      channelName: string,
      eventName: string,
      callback: PusherEventHandler | null = null,
      { retainPayload = true }: BindEventOptions = {},
    ): PusherEventHandler | null {
      const channel = this.subscribedChannels[channelName];
      if (channel) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handler = (data: any) => {
          // pusher-js raises its own `pusher:` events with whatever the server sent, which can be
          // nothing; only a server event's missing payload is worth dropping.
          if ((data === undefined || data === null) && !eventName.startsWith('pusher:')) {
            console.warn(
              `Received undefined or null data for event '${eventName}' on channel '${channelName}'.`,
            );
            return;
          }

          if (retainPayload) {
            this.messages.push({
              channel: channelName,
              event: eventName,
              data: data,
              timestamp: new Date().toISOString(),
            });
          }
          console.log(`Event '${eventName}' received on channel '${channelName}':`);

          if (callback && typeof callback === 'function') {
            callback(data);
          }
        };
        channel.bind(eventName, handler);
        console.log(`Bound event '${eventName}' on channel '${channelName}'.`);
        return handler;
      } else {
        console.error(`Cannot bind event. Channel '${channelName}' is not subscribed.`);
        return null;
      }
    },

    /** Unbinds one handler `bindEvent` returned, leaving every other binding on the channel. */
    unbindEvent(channel: PusherChannel, eventName: string, handler: PusherEventHandler) {
      channel.unbind(eventName, handler);
    },

    /**
     * Unsubscribes through the channel object itself, so it works whatever a logout or a store reset
     * has done to `pusherInstance` and `subscribedChannels` by the time its subscriber is unmounted.
     */
    leaveChannel(channel: PusherChannel) {
      channel.pusher.unsubscribe(channel.name);
      if (toRaw(this.subscribedChannels[channel.name]) === toRaw(channel)) {
        delete this.subscribedChannels[channel.name];
        delete this.channelAuthorizers[channel.name];
      }
    },

    /**
     * An organisation switch's share of this store: what it holds about the organisation left
     * behind. The connection, its channels and their signers outlive the switch, as the socket does.
     */
    clearForOrganisationSwitch() {
      this.messages = [];
      this.showModal = false;
      this.roleMessage = {} as RoleMessageData;
    },

    /**
     * Unsubscribes from a specified Pusher channel.
     * @param {string} channelName - The name of the channel to unsubscribe from.
     */
    unsubscribeFromChannel(channelName: string) {
      if (!this.pusherInstance) {
        console.warn('Pusher instance not initialized. No channels to unsubscribe.');
        return;
      }
      if (this.subscribedChannels[channelName]) {
        this.pusherInstance.unsubscribe(channelName);
        delete this.subscribedChannels[channelName];
        console.log(`Unsubscribed from channel: ${channelName}`);
      } else {
        console.warn(`Not subscribed to channel: ${channelName}. No action taken.`);
      }
    },

    /**
     * Disconnects the Pusher client completely.
     * This will also unsubscribe from all channels implicitly.
     */
    disconnectPusher() {
      if (this.pusherInstance) {
        this.pusherInstance.disconnect();
        this.pusherInstance = null;
        this.subscribedChannels = {};
        this.channelAuthorizers = {};
        this.messages = [];
        this.connectionStatus = 'disconnected';
        console.log('Pusher client completely disconnected.');
      }
    },
  },
});
